import { SystemStatus } from './ops'
import { KeyDict } from '.'

export enum StreamType {
  Caster = 'caster',
  Broadcast = 'broadcast',
  Output = 'output',
  Stream = 'stream',
  NonCaster = 'noncaster',
  Url = 'url',
  Playlist = 'playlist',
  Moderator = 'moderator'
}

export enum StreamStatusCode {
  StreamGood = 'stream-good',
  StreamWarning = 'stream-warning',
  StreamBooting = 'stream-booting',
  StreamError = 'stream-error',
  SRTError = 'srt-error',
  StreamNotConnected = 'stream-not-connected',
  OutputGood = 'output-good',
  OutputNotConnected = 'output-not-connected',
  OutputLowBandwidth = 'output-low-bandwidth',
  CasterPresent = 'caster-present',
  CasterNotPresent = 'caster-not-present',
  CasterLowFPS = 'caster-low-fps',
  CasterConnecting = 'caster-connecting',
  CasterCastingIssues = 'caster-casting-issues',
  CasterNotCastingBadNetwork = 'caster-not-casting-bad-network',
  CasterError = 'caster-error',
  CasterErrorNotPresent = 'caster-error-not-present',
  CrewPresent = 'crew-present',
  CrewNotPresent = 'crew-not-present',
  CrewError = 'crew-not-present',
  CasterCasting = 'caster-casting',
  None = ''
}
export interface SrtInfo {
  nimbleserver: string,
  ip? : string,
  port: number,
  mode?: 'caller' | 'listen' | 'rendezvous'
}

export interface SrtMetric {
  link?: {
    rtt: string
  },
  recv?: {
    mbpsRate: string
  },
  send?: {
    mbpsRate: string
  },
  time?: number
}

export interface Stream {
  id?: string,
  active?: boolean,
  persistent?: boolean,
  connected?: boolean,
  deleted?: boolean,
  pubts?: number,
  srcurl: string,
  team?: string,
  team_id?: string,
  type: StreamType,
  url?: string,
  stream?: string,
  user?: string,
  user_id?: string,
  name: string,
  templateId?: string,
  quality?: {
    framerate?: string,
    resolution?: string
  }
  reencode?: boolean,
  volume?: number,
  volumeLeft?: number,
  volumeRight?: number,
  ngcvp_status?: SystemStatus,
  streamAvailable?: boolean,
  leftrightsplit?: boolean,
  streamname?: string,
  framerate?: string,
  resolution?: string, // TODO make into a type
  rate?: StreamRate,
  variants?: { [ id: string ]: StreamVariant },
  feedName?: string,
  feedNames?: string[],
  start_time?: number,
  srt_state?: string,
  srt_info?: SrtInfo,
  srt_msg?: string,
  srt_metrics? : SrtMetric,
  interlaced?: boolean,
  gop_size?: number,
  auth_info?: {
    type: string,
    ingest_url: string,
    error?: string
  }
}

export interface StreamStatus {
  ngcvp_status: SystemStatus,
  stream: string
}

export interface SourceStream {
  active?: boolean,
  id?: string,
  connected?: boolean,
  leftMuted?: boolean,
  cef_feed_id?: string,
  name?: string,
  ngcvp_status?: SystemStatus,
  rightMuted?: boolean,
  type: StreamType,
  muted?: boolean,
  volume?: number,
  volumeLeft?: number,
  volumeRight?: number,
  user_id?: string,
  start_time?: number,
  delay?: number,
  srt_state?: string,
  session_id?: string,
  cast?: string
}

export type StreamVariantType = 'original' | 'lowratenosrt' | 'srt' | 'lowrate' | 'lowratecaster'

export interface StreamVariant {
  audioinfo?: {
    channels?: number,
    codec?: string,
    frequency?: number,
    objectType?: string,
    samplePerFrame?: number
  }
  hasaudio: boolean,
  hasvideo: boolean,
  mongoid: string,
  pubtime: number,
  server: string,
  type?: StreamVariantType,
  url: string,
  videoinfo?: {
    codec?: string,
    displaySize?: string,
    frameRate?: number,
    frameSize?: string,
    level?: number,
    profile?: string
  }
}


export enum talkbackTargetType {
  ALL = 'ALL',
  CREW = 'CREW',
  NONE = 'NONE'
}

export interface CastStream {
  id: string,
  srcObject: MediaStream | null,
  type: StreamType,
  leftrightsplit: boolean
}

export interface StreamDoc {
  active: boolean,
  deleted: boolean,
  leftrightsplit: boolean,
  name: string,
  persistent: boolean,
  srcurl: string,
  team: string,
  team_id: string,
  type: StreamType,
  talkbackTarget?: string, // specific caster id or NONE
  variants: { [ id: string ]: StreamVariant }
}

export interface CustomRtmpDestination {
  url: string,
  streamname: string,
  socialmedia?: string,
  valid: boolean,
  start_time: number,
  type: StreamType,
  rate?: StreamRate,
  active?: boolean
}

export interface StreamRate {
  fps?: string,
  width?: number,
  height?: number,
  video_bitrate?: number,
  adjusted?: boolean,
  interlaced?: boolean,
  gop_size?: number
}

export interface StreamsManager {
  [ streamKey: string ]: Stream
}

export class DefaultStream {
  name: string = ''
  url: string = ''
  description: string = ''
  leftrightsplit: boolean = false
  add_to_new_teams: boolean = false
  templateId: string = ''

  constructor (data: KeyDict<any>) {
    this.name = data.name ?? ''
    this.url = data.url ?? ''
    this.description = data.description ?? ''
    this.leftrightsplit = data.leftrightsplit ?? false
    this.add_to_new_teams = data.add_to_new_teams ?? false
    this.templateId = data.templateId ?? ''
  }

  toStream (): Stream {
    const data: Stream = {
      deleted: false,
      srcurl: this.url,
      type: StreamType.Broadcast,
      name: this.name,
      leftrightsplit: this.leftrightsplit,
    }
    if (this.templateId) data.templateId = this.templateId
    return data
  }
}

export interface OutputSourceStream {
  streamid: string,
  streamidurl: string,
  streamname: string,
  streamkey: string,
  streamurl: string,
  status: string,
  type: StreamType,
  srcurl: string,
  stream: Stream,
  casterid?: string,
  casteridurl?: string,
  casterfirstname?: string,
  casterlastname?: string,
  casterregion?: string,
}
